import { Card, Skeleton } from "antd";
import { brand, border, neutral, radius, semantic, shadow, typography } from "../../theme/tokens";

/**
 * Thẻ số liệu dùng chung cho các trang danh sách / chi tiết.
 *
 * Nguyên tắc trình bày (chốt để mọi màn giống nhau):
 *  - Thẻ luôn nền trắng + viền hairline. Không gradient, không nền màu.
 *  - Màu chỉ nằm ở ô icon, và lấy từ `semantic`/`brand` trong theme/tokens.
 *  - Con số luôn để neutral[900]. Không tô xanh/đỏ theo giá trị — số liệu tự nói,
 *    tô màu khắp nơi làm mất điểm nhấn và khiến trang trông màu mè.
 *
 * @param {import("react").ReactNode} icon
 * @param {string} label
 * @param {import("react").ReactNode} value
 * @param {"brand"|"success"|"warning"|"danger"|"info"|"neutral"} tone  Tông của ô icon.
 * @param {boolean} loading
 * @param {import("react").ReactNode} extra  Dòng phụ dưới con số (thanh tiến độ, chú thích...).
 */

const TONES = {
    brand: { fg: brand.accent, bg: brand.primarySubtle },
    success: { fg: semantic.success, bg: semantic.successSubtle },
    warning: { fg: semantic.warning, bg: semantic.warningSubtle },
    danger: { fg: semantic.danger, bg: semantic.dangerSubtle },
    info: { fg: semantic.info, bg: semantic.infoSubtle },
    neutral: { fg: neutral[500], bg: neutral[100] },
};

export default function StatCard({ icon, label, value, tone = "brand", loading = false, extra }) {
    const { fg, bg } = TONES[tone] || TONES.brand;

    return (
        <Card
            style={{
                height: "100%",
                borderRadius: radius.md,
                border: border.hairline,
                boxShadow: shadow.xs,
            }}
            styles={{ body: { padding: 16 } }}
        >
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                {icon && (
                    <div
                        style={{
                            width: 40,
                            height: 40,
                            borderRadius: radius.md,
                            background: bg,
                            color: fg,
                            fontSize: 18,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                        }}
                    >
                        {icon}
                    </div>
                )}

                <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                        style={{
                            fontSize: typography.size.base,
                            color: neutral[500],
                            marginBottom: 2,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        {label}
                    </div>

                    {loading ? (
                        <Skeleton.Input active size="small" style={{ width: 90, height: 26 }} />
                    ) : (
                        <>
                            <div
                                style={{
                                    fontSize: typography.size["2xl"],
                                    fontWeight: typography.weight.bold,
                                    color: neutral[900],
                                    lineHeight: typography.leading.tight,
                                }}
                            >
                                {value}
                            </div>
                            {extra}
                        </>
                    )}
                </div>
            </div>
        </Card>
    );
}
